import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
// @mui
import {
  Typography, Box, Stack, TextField, Button, Modal,
  Autocomplete, Checkbox, IconButton, Divider, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpacityIcon from '@mui/icons-material/Opacity';
import StoreIcon from '@mui/icons-material/Store';
import GridOnIcon from '@mui/icons-material/GridOn';
import PowerIcon from '@mui/icons-material/Power';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ModalAddFornecedor from './ModalAddFornecedor';
import ModalImportarKit from './ModalImportarKit';

// api
import {
  getModulos, getInversoresBomb, getTipoDisjuntores,
  getPotenciaDisjuntores, getFornecedores, getHspIbge, getHsp,
  getEstados, getMunicipios,
} from './api';
import { cores, campoEscuro } from '../styles/styleUtils';
import usePerformanceRatio from '../hooks/usePerformanceRatio';
import PerformanceRatioInfo from '../components/PerformanceRatioInfo';

// ── Estilos ────────────────────────────────────────────────────────────
const style = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70vw', height: '95vh',
  '@media screen and (max-width: 600px)': { width: '100vw', height: '90vh' },
  bgcolor: 'transparent', border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24, p: 2, display: 'flex', flexDirection: 'column',
  overflowY: 'auto', background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`, color: cores.textoClaro,
};

const sf = campoEscuro('14vw');
const sfMd = campoEscuro('20vw');
const sfLg = campoEscuro('30vw');
// Usado apenas nos campos do Dialog "Composição de Preços" (fundo branco).
// Rótulo no padrão MUI (flutua sozinho ao focar/preencher), sem forçar cor branca —
// o antigo sfFull forçava rótulo branco (feito p/ o painel escuro do formulário
// principal) e ficava invisível sobre o fundo branco deste Dialog.
const sfFull = { backgroundColor: 'white', width: '100%' };

const IconButtonWrapper = styled('div')({
  display: 'flex', alignItems: 'center', marginBottom: 8,
});

// Cartão sutil pra agrupar cada seção do formulário sobre o painel escuro
const sxCard = { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 2, mb: 2 };

// Ícone de calculadora nos campos "disabled" (valor calculado automaticamente,
// não é um campo vazio) — deixa claro que o campo não é editável por acaso.
const calcAdornment = {
  startAdornment: (
    <InputAdornment position="start">
      <CalculateIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.35)' }} />
    </InputAdornment>
  ),
};

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const emptyComp = {
  custogerador: '',
  materialeletrico: '',
  tipomaoobra: 'placa', // 'placa' | 'kwp' — modo de cálculo da mão de obra de montagem
  moporplaca: '',
  movalorkwp: '',
  art: '',
  projeto: '',
  custosadicional: '',
  comissao: '',
  impostos: '',
  margembruta: '',
};

// ── Componente ─────────────────────────────────────────────────────────
const AddKitBombeamento = React.forwardRef((props, ref) => {
  const usuario = JSON.parse(localStorage.getItem('userData'));
  const isSupervisor = usuario?.supervisor;

  const [produto, setProduto] = useState(null);
  const estadoPR = usePerformanceRatio(null, 'kit');
  const [tipodisjuntor, setTipoDisjuntor] = useState([]);
  const [potenciadisjuntor, setPotenciadisjuntor] = useState([]);
  const [rowsModulos, setRowsModulos] = useState([]);
  const [rowsInversores, setRowsInversores] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [modalAddFornecedor, setModalAddFornecedor] = useState(false);
  const [modalImportarKit, setModalImportarKit] = useState(false);

  // Estados/Cidades para Dados de Geração
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);

  // Modal Composição de Preços
  const [modalComp, setModalComp] = useState(false);
  const [comp, setComp] = useState(emptyComp);

  // ── Fetch inicial ──────────────────────────────────────────────────
  useEffect(() => { fetchFornecedores(); }, []);
  const fetchFornecedores = async () => {
    const resultado = await getFornecedores();
    setFornecedores(resultado);
  };

  useEffect(() => {
    const fetchDataAndInitialize = async () => {
      const tipoDisjuntorData     = await getTipoDisjuntores();
      const potenciaDisjuntorData = await getPotenciaDisjuntores();
      const modulosData           = await getModulos();
      const inversoresData        = await getInversoresBomb();

      setTipoDisjuntor(tipoDisjuntorData);
      setPotenciadisjuntor(potenciaDisjuntorData);
      setRowsModulos(modulosData);
      setRowsInversores(inversoresData);

      if (!props.produto) {
        const contexto = props.contextoDimensionamento || {};
        const moduloContexto =
          modulosData.find((m) => m?._id === contexto.modulo?._id) ||
          contexto.modulo ||
          modulosData[0] ||
          null;
        const inversorContexto =
          inversoresData.find((i) => i?._id === contexto.inversor?._id) ||
          contexto.inversor ||
          inversoresData[0] ||
          null;
        const potenciaModuloW = Number(moduloContexto?.potencia || 0);
        const potenciaNecessaria = Number(contexto.potenciaNecessaria || 0);
        const qtdSugerida =
          potenciaModuloW > 0 && potenciaNecessaria > 0
            ? Math.max(1, Math.ceil((potenciaNecessaria * 1000) / potenciaModuloW))
            : 1;

        setProduto({
          tipokat: 'bombeamento',
          id: null, nome: '', ativo: true,
          fornecedor: contexto.fornecedor || null,
          modulo: moduloContexto,
          quantidademodulos: qtdSugerida,
          potenciacc: moduloContexto ? ((moduloContexto.potencia / 1000) * qtdSugerida).toFixed(2) : 0,
          inversor: inversorContexto,
          quantidadeinversores: 1,
          potenciainversor: inversorContexto?.potencia || 0,
          disjuntorminino: {
            tipodisjuntor: tipoDisjuntorData[1] || null,
            potenciadisjuntor: potenciaDisjuntorData[0] || null,
          },
          descontomaximo: '',
          custogerador: '',
          materialeletrico: '',
          moporplaca: '',
          art: '',
          projeto: '',
          custosadicional: '',
          comissao: '',
          impostos: '',
          margembruta: '',
          fatorCartao: 1.1428,
          precovenda: 0,
          valorcartao: 0,
          geracaonorte: 0,
          geracaolesteoeste: 0,
          geracaosul: 0,
          cidadeGeracao: contexto.cidadeGeracao || null,
          estadoGeracao: contexto.estadoGeracao || null,
          hspMedia: contexto.hspMedia || 0,
        });
      }
    };
    fetchDataAndInitialize();
  }, [props.contextoDimensionamento, props.produto]);

  useEffect(() => {
    if (props.produto) {
      setProduto(props.produto);
      setComp({
        custogerador:     props.produto.custogerador     != null ? String(props.produto.custogerador)     : '',
        materialeletrico: props.produto.materialeletrico != null ? String(props.produto.materialeletrico) : '',
        tipomaoobra:      props.produto.tipomaoobra       != null ? props.produto.tipomaoobra              : 'placa',
        moporplaca:       props.produto.moporplaca       != null ? String(props.produto.moporplaca)       : '',
        movalorkwp:       props.produto.movalorkwp       != null ? String(props.produto.movalorkwp)       : '',
        art:              props.produto.art              != null ? String(props.produto.art)              : '',
        projeto:          props.produto.projeto          != null ? String(props.produto.projeto)          : '',
        custosadicional:  props.produto.custosadicional  != null ? String(props.produto.custosadicional)  : '',
        comissao:         props.produto.comissao         != null ? String(props.produto.comissao)         : '',
        impostos:         props.produto.impostos         != null ? String(props.produto.impostos)         : '',
        margembruta:      props.produto.margembruta      != null ? String(props.produto.margembruta)      : '',
      });
    }
  }, [props.produto]);

  // ── Potência CC ────────────────────────────────────────────────────
  useEffect(() => {
    if (produto?.modulo && produto?.quantidademodulos) {
      const potCC = (produto.modulo.potencia / 1000 * produto.quantidademodulos).toFixed(2);
      setProduto((prev) => ({ ...prev, potenciacc: potCC }));
    }
  }, [produto?.quantidademodulos, produto?.modulo]);

  // ── Potência Inversor ──────────────────────────────────────────────
  useEffect(() => {
    if (produto?.inversor && produto?.quantidadeinversores) {
      const potInv = (produto.inversor.potencia * produto.quantidadeinversores).toFixed(2);
      setProduto((prev) => ({ ...prev, potenciainversor: potInv }));
    }
  }, [produto?.quantidadeinversores, produto?.inversor]);

  // ── Geração por cidade ─────────────────────────────────────────────
  useEffect(() => {
    if (!produto?.hspMedia || !produto?.potenciacc || !estadoPR.pronto) return;
    const norte = produto.hspMedia * parseFloat(produto.potenciacc) * 30 * estadoPR.pr;
    setProduto((prev) => ({
      ...prev,
      geracaonorte:      parseFloat(norte.toFixed(1)),
      pr: estadoPR.pr,
      geracaolesteoeste: parseFloat((norte * 0.85).toFixed(1)),
      geracaosul:        parseFloat((norte * 0.70).toFixed(1)),
    }));
  }, [produto?.hspMedia, produto?.potenciacc, estadoPR.pr, estadoPR.pronto]);

  // ── Estados (cache local, antes buscava direto do IBGE) ─────────────
  useEffect(() => {
    getEstados().then((data) => setEstados((data || []).sort((a, b) => a.nome.localeCompare(b.nome))));
  }, []);

  useEffect(() => {
    if (produto?.estadoGeracao) {
      getMunicipios(produto.estadoGeracao.sigla)
        .then((data) => setCidades((data || []).sort((a, b) => a.nome.localeCompare(b.nome))));
    }
  }, [produto?.estadoGeracao]);

  const handleCidadeGeracao = async (cidade) => {
    if (!cidade) return;
    let hspData = null;
    try {
      if (cidade.id) hspData = await getHspIbge(Number(cidade.id));
      if (!hspData && produto?.estadoGeracao) {
        hspData = await getHsp(cidade.nome, produto.estadoGeracao.sigla);
      }
    } catch (e) { console.warn('HSP:', e); }
    setProduto((prev) => ({
      ...prev,
      cidadeGeracao: cidade,
      hspMedia: hspData?.hspMedia || 0,
    }));
  };

  // ── Composição de Preços ───────────────────────────────────────────
  const calcComp = () => {
    const cg     = parseFloat(comp.custogerador    || 0);
    const me     = parseFloat(comp.materialeletrico || 0);
    const mo     = comp.tipomaoobra === 'kwp'
      ? parseFloat(comp.movalorkwp || 0) * parseFloat(produto?.potenciacc || 0)
      : parseFloat(comp.moporplaca || 0) * (produto?.quantidademodulos || 0);
    const art    = parseFloat(comp.art              || 0);
    const proj   = parseFloat(comp.projeto          || 0);
    const ca     = parseFloat(comp.custosadicional  || 0);
    const comPct = parseFloat(comp.comissao         || 0) / 100;
    const impPct = parseFloat(comp.impostos         || 0) / 100;
    const mb     = parseFloat(comp.margembruta      || 0) / 100;

    const totalMat  = cg + me;
    const totalServ = mo + art + proj;
    const custoBase = totalMat + totalServ + ca;
    const pv        = custoBase * mb;
    const comissaoR = pv * comPct;
    const impostosR = pv * impPct;
    const totalDesp = custoBase + comissaoR + impostosR;
    const lucroBruto = pv - totalDesp;
    const lucroPct   = pv > 0 ? (lucroBruto / pv * 100) : 0;

    return { totalMat, totalServ, mo, comissaoR, impostosR, totalDesp, pv, lucroBruto, lucroPct };
  };

  const salvarComposicao = () => {
    const { pv, lucroPct } = calcComp();
    const fator = parseFloat(produto?.fatorCartao || 1.1428);
    setProduto((prev) => ({
      ...prev,
      ...comp,
      precovenda:  parseFloat(pv.toFixed(2)),
      valorcartao: parseFloat((pv * fator).toFixed(2)),
      margemlucro: parseFloat(lucroPct.toFixed(2)),
    }));
    setModalComp(false);
  };

  const handleChange = (e) => {
    const v = e.target.type === 'number'
      ? (e.target.value !== '' ? Number(e.target.value) : '')
      : e.target.value;
    setProduto({ ...produto, [e.target.id]: v });
  };

  // Importar cadastro existente: copia os dados do kit selecionado para o
  // formulário atual (fica editável), sem herdar _id nem dados de geração
  // (que são específicos do local de instalação de cada kit).
  const handleImportarKit = (kitImportado) => {
    const {
      _id, estadoGeracao, cidadeGeracao, hspMedia,
      geracaonorte, geracaolesteoeste, geracaosul,
      ...resto
    } = kitImportado;
    setProduto((prev) => ({ ...prev, ...resto }));
    setComp({
      custogerador:     kitImportado.custogerador     != null ? String(kitImportado.custogerador)     : '',
      materialeletrico: kitImportado.materialeletrico != null ? String(kitImportado.materialeletrico) : '',
      tipomaoobra:      kitImportado.tipomaoobra      != null ? kitImportado.tipomaoobra               : 'placa',
      moporplaca:       kitImportado.moporplaca       != null ? String(kitImportado.moporplaca)       : '',
      movalorkwp:       kitImportado.movalorkwp       != null ? String(kitImportado.movalorkwp)       : '',
      art:              kitImportado.art              != null ? String(kitImportado.art)              : '',
      projeto:          kitImportado.projeto          != null ? String(kitImportado.projeto)          : '',
      custosadicional:  kitImportado.custosadicional  != null ? String(kitImportado.custosadicional)  : '',
      comissao:         kitImportado.comissao         != null ? String(kitImportado.comissao)         : '',
      impostos:         kitImportado.impostos         != null ? String(kitImportado.impostos)         : '',
      margembruta:      kitImportado.margembruta      != null ? String(kitImportado.margembruta)      : '',
    });
    setModalImportarKit(false);
  };

  const handleConfirm = () => {
    if (!estadoPR.pronto) return;
    props.onConfirm({ ...produto, pr: estadoPR.pr, tipokat: 'bombeamento' });
    setProduto(null);
    props.onClose();
  };

  const onClose = () => {
    setProduto(null);
    props.onClose();
  };

  const res = produto ? calcComp() : null;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <>
      <Modal open={props.open} onClose={onClose}>
        {produto ? (
          <Box sx={style}>
            <PerformanceRatioInfo estado={estadoPR} />
            <Typography variant="h6" fontWeight="bold" mb={1} sx={{ color: cores.laranja }}>
              <OpacityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Kit Bombeamento
            </Typography>

            {/* ══════════ Dados do Kit ══════════ */}
            <Box sx={sxCard}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <WidgetsIcon fontSize="small" sx={{ color: cores.laranja }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Dados do kit</Typography>
                </Stack>
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => setModalImportarKit(true)}
                  sx={{ color: cores.textoClaro, borderColor: cores.laranja, '&:hover': { borderColor: cores.laranjaEscuro, backgroundColor: 'rgba(209,102,9,0.15)' } }}>
                  Importar cadastro existente
                </Button>
              </Stack>

              {/* Nome + Fornecedor */}
              <Stack direction="row" justifyContent="space-between" mb={1.5}>
                <TextField size="small" id="nome" label="Nome" value={produto.nome}
                  onChange={handleChange} sx={{ ...sfLg, mb: 0 }} />
                <IconButtonWrapper>
                  <Autocomplete disablePortal id="fornecedor" value={produto.fornecedor || null}
                    options={fornecedores} getOptionLabel={(o) => o.nome}
                    isOptionEqualToValue={(o, v) => o._id === v._id} sx={sfMd}
                    renderInput={(p) => (
                      <TextField {...p} label="Fornecedor" InputProps={{
                        ...p.InputProps,
                        startAdornment: <InputAdornment position="start"><StoreIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                      }} />
                    )} size="small"
                    onChange={(_, v) => setProduto({ ...produto, fornecedor: v })} />
                  <IconButton onClick={() => setModalAddFornecedor(true)}><AddCircleIcon /></IconButton>
                </IconButtonWrapper>
              </Stack>

              {/* Módulo */}
              <Stack direction="row" gap={1} alignItems="center" mb={1.5}>
                <Autocomplete disablePortal value={produto.modulo || null}
                  options={rowsModulos}
                  getOptionLabel={(o) => `Módulo ${o.potencia}W ${o.marca}`}
                  isOptionEqualToValue={(o, v) => o._id === v._id} sx={sfLg}
                  renderInput={(p) => (
                    <TextField {...p} label="Módulo" InputProps={{
                      ...p.InputProps,
                      startAdornment: <InputAdornment position="start"><GridOnIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                    }} />
                  )} size="small"
                  onChange={(_, v) => setProduto({ ...produto, modulo: v })} />
                <TextField size="small" id="quantidademodulos" label="Qtd."
                  inputProps={{ type: 'number' }} sx={{ ...sf, width: '7vw' }}
                  value={produto.quantidademodulos} onChange={handleChange} />
                <TextField size="small" label="Potência CC (kWp)" disabled
                  sx={{ ...sf, width: '10vw' }} InputProps={calcAdornment} value={produto.potenciacc} />
              </Stack>

              {/* Inversor Bombeamento */}
              <Stack direction="row" gap={1} alignItems="center" mb={1.5}>
                <Autocomplete disablePortal value={produto.inversor || null}
                  options={rowsInversores}
                  getOptionLabel={(o) => `${o.modelo} — ${o.potencia} CV`}
                  isOptionEqualToValue={(o, v) => o._id === v._id} sx={sfLg}
                  renderInput={(p) => (
                    <TextField {...p} label="Inversor Bombeamento" InputProps={{
                      ...p.InputProps,
                      startAdornment: <InputAdornment position="start"><PowerIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                    }} />
                  )} size="small"
                  onChange={(_, v) => setProduto({ ...produto, inversor: v, potenciainversor: v?.potencia || 0 })} />
                <TextField size="small" id="quantidadeinversores" label="Qtd."
                  inputProps={{ type: 'number' }} sx={{ ...sf, width: '7vw' }}
                  value={produto.quantidadeinversores} onChange={handleChange} />
                <TextField size="small" label="Potência Inv. (CV)" disabled
                  sx={{ ...sf, width: '10vw' }} InputProps={calcAdornment} value={produto.potenciainversor} />
              </Stack>

              {/* Disjuntor */}
              <Stack direction="row" gap={1} mb={1.5}>
                <Autocomplete disablePortal
                  value={produto.disjuntorminino?.tipodisjuntor || null}
                  options={tipodisjuntor} getOptionLabel={(o) => o.nome}
                  isOptionEqualToValue={(o, v) => o._id === v._id} sx={sf} size="small"
                  renderInput={(p) => (
                    <TextField {...p} label="Tipo Disjuntor" InputProps={{
                      ...p.InputProps,
                      startAdornment: <InputAdornment position="start"><FlashOnIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                    }} />
                  )}
                  onChange={(_, v) => setProduto({ ...produto, disjuntorminino: { ...produto.disjuntorminino, tipodisjuntor: v } })} />
                <Autocomplete disablePortal
                  value={produto.disjuntorminino?.potenciadisjuntor || null}
                  options={potenciadisjuntor} getOptionLabel={(o) => `${o.valor}A`}
                  isOptionEqualToValue={(o, v) => o._id === v._id} sx={sf} size="small"
                  renderInput={(p) => <TextField {...p} label="Corrente" />}
                  onChange={(_, v) => setProduto({ ...produto, disjuntorminino: { ...produto.disjuntorminino, potenciadisjuntor: v } })} />
              </Stack>

              {/* Ativo */}
              <Stack direction="row" alignItems="center">
                <Checkbox checked={produto.ativo}
                  onChange={() => setProduto({ ...produto, ativo: !produto.ativo })} />
                <Typography variant="body2">Ativo</Typography>
              </Stack>
            </Box>

            {/* ══════════ Dados de Geração ══════════ */}
            <Box sx={sxCard}>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <WbSunnyIcon fontSize="small" sx={{ color: cores.laranja }} />
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Dados de geração</Typography>
              </Stack>
              <Stack direction="row" gap={2} mb={1.5}>
                <Autocomplete disablePortal value={produto.estadoGeracao || null} options={estados}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => `${o.nome} - ${o.sigla}`} sx={sfMd} size="small"
                  renderInput={(p) => (
                    <TextField {...p} label="Estado" InputProps={{
                      ...p.InputProps,
                      startAdornment: <InputAdornment position="start"><PublicIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                    }} />
                  )}
                  onChange={(_, v) => setProduto({ ...produto, estadoGeracao: v, cidadeGeracao: null, hspMedia: 0 })} />
                <Autocomplete disablePortal value={produto.cidadeGeracao || null} options={cidades}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  getOptionLabel={(o) => o.nome} sx={sfMd} size="small"
                  renderInput={(p) => (
                    <TextField {...p} label="Cidade" InputProps={{
                      ...p.InputProps,
                      startAdornment: <InputAdornment position="start"><LocationCityIcon fontSize="small" sx={{ color: cores.laranja }} /></InputAdornment>,
                    }} />
                  )}
                  onChange={(_, v) => handleCidadeGeracao(v)} />
                {produto.hspMedia > 0 && (
                  <TextField size="small" label="HSP Médio" disabled
                    value={`${produto.hspMedia?.toFixed(2)} h/dia`} sx={sf} InputProps={calcAdornment} />
                )}
              </Stack>

              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }} mb={1} display="block">Produção média mensal (kWh)</Typography>
              <Stack direction="row" gap={2}>
                <TextField size="small" label="Norte/Noroeste/Nordeste" disabled
                  value={produto.geracaonorte || 0} sx={sf} InputProps={calcAdornment} />
                <TextField size="small" label="Leste/Oeste" disabled
                  value={produto.geracaolesteoeste || 0} sx={sf} InputProps={calcAdornment} />
                <TextField size="small" label="Sul/Sudoeste/Sudeste" disabled
                  value={produto.geracaosul || 0} sx={sf} InputProps={calcAdornment} />
              </Stack>
            </Box>

            {/* ══════════ Preço de Venda ══════════ */}
            <Box sx={sxCard}>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Preço de venda</Typography>
              </Stack>
              <Stack direction="row" gap={2} mb={1.5} flexWrap="wrap">
                <TextField size="small" label="Valor à Vista (R$)" disabled
                  value={fmtBRL(produto.precovenda)} sx={sf} InputProps={calcAdornment} />
                {isSupervisor && (
                  <>
                    <TextField size="small" label="Lucro Projetado (%)" disabled
                      value={produto.margemlucro ? `${produto.margemlucro}%` : '—'} sx={sf} InputProps={calcAdornment} />
                    <TextField size="small" id="descontomaximo" label="Desconto Habilitado (%)"
                      inputProps={{ type: 'number' }} sx={sf}
                      value={produto.descontomaximo} onChange={handleChange} />
                  </>
                )}
                <TextField size="small" label="Valor Cartão (R$)" disabled
                  value={fmtBRL(produto.valorcartao)} sx={sf} InputProps={calcAdornment} />
                {isSupervisor && (
                  <TextField size="small" id="fatorCartao" label="Fator Cartão"
                    inputProps={{ type: 'number', step: '0.0001' }} sx={sf}
                    value={produto.fatorCartao || 1.1428} onChange={handleChange} />
                )}
              </Stack>

              {/* Botão Composição de Preços — só supervisor */}
              {isSupervisor && (
                <Button variant="outlined" startIcon={<CalculateIcon />}
                  onClick={() => setModalComp(true)} sx={{ alignSelf: 'flex-start', color: cores.textoClaro, borderColor: cores.laranja, '&:hover': { borderColor: cores.laranjaEscuro, backgroundColor: 'rgba(209,102,9,0.15)' } }}>
                  Composição de Preços
                </Button>
              )}
            </Box>

            {/* ── Botões principais ── */}
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 'auto', pt: 1 }}>
              <Button variant="contained" onClick={onClose} color="error">Cancelar</Button>
              <Button variant="contained" color="success" onClick={handleConfirm} disabled={!estadoPR.pronto}>Salvar</Button>
            </Stack>

            {modalAddFornecedor && (
              <ModalAddFornecedor open={modalAddFornecedor}
                onClose={() => setModalAddFornecedor(false)}
                onConfirm={() => { fetchFornecedores(); setModalAddFornecedor(false); }} />
            )}

            {modalImportarKit && (
              <ModalImportarKit open={modalImportarKit} tipo="bombeamento"
                onClose={() => setModalImportarKit(false)}
                onConfirm={handleImportarKit} />
            )}
          </Box>
        ) : <></>}
      </Modal>

      {/* ── Modal Composição de Preços ── */}
      <Dialog open={modalComp} onClose={() => setModalComp(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Composição do Preço de Venda — Bombeamento</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <AttachMoneyIcon fontSize="small" sx={{ color: cores.laranja }} />
              <Typography variant="subtitle2" sx={{ color: cores.laranja }}>Custos</Typography>
            </Stack>
            <Stack direction="row" gap={2}>
              <TextField size="small" label="Custo Gerador (R$)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.custogerador}
                onChange={(e) => setComp({ ...comp, custogerador: e.target.value })} />
              <TextField size="small" label="Material Elétrico (R$)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.materialeletrico}
                onChange={(e) => setComp({ ...comp, materialeletrico: e.target.value })} />
            </Stack>
            <Stack gap={1}>
              <ToggleButtonGroup size="small" exclusive value={comp.tipomaoobra || 'placa'}
                onChange={(_, v) => v && setComp({ ...comp, tipomaoobra: v })}>
                <ToggleButton value="placa">Por placa</ToggleButton>
                <ToggleButton value="kwp">Por kWp</ToggleButton>
              </ToggleButtonGroup>
              {comp.tipomaoobra === 'kwp' ? (
                <Stack direction="row" gap={2}>
                  <TextField size="small" label="M.O. por kWp (R$)"
                    sx={{ backgroundColor: 'white', width: '60%' }}
                    inputProps={{ type: 'number' }} value={comp.movalorkwp}
                    onChange={(e) => setComp({ ...comp, movalorkwp: e.target.value })} />
                  <TextField size="small"
                    label={`Total M.O. (${produto?.potenciacc || 0} kWp)`}
                    disabled sx={{ backgroundColor: 'white', width: '40%' }}
                    value={fmtBRL(parseFloat(comp.movalorkwp || 0) * parseFloat(produto?.potenciacc || 0))} />
                </Stack>
              ) : (
                <Stack direction="row" gap={2}>
                  <TextField size="small" label="M.O. por Placa (R$)"
                    sx={{ backgroundColor: 'white', width: '60%' }}
                    inputProps={{ type: 'number' }} value={comp.moporplaca}
                    onChange={(e) => setComp({ ...comp, moporplaca: e.target.value })} />
                  <TextField size="small"
                    label={`Total M.O. (${produto?.quantidademodulos || 0} placas)`}
                    disabled sx={{ backgroundColor: 'white', width: '40%' }}
                    value={fmtBRL(parseFloat(comp.moporplaca || 0) * (produto?.quantidademodulos || 0))} />
                </Stack>
              )}
            </Stack>
            <Stack direction="row" gap={2}>
              <TextField size="small" label="ART (R$)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.art}
                onChange={(e) => setComp({ ...comp, art: e.target.value })} />
              <TextField size="small" label="Projeto (R$)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.projeto}
                onChange={(e) => setComp({ ...comp, projeto: e.target.value })} />
            </Stack>
            <TextField size="small" label="Custos Adicionais (R$)" sx={sfFull}
              inputProps={{ type: 'number' }} value={comp.custosadicional}
              onChange={(e) => setComp({ ...comp, custosadicional: e.target.value })} />

            <Divider />

            <Stack direction="row" alignItems="center" gap={1}>
              <ArrowUpwardIcon fontSize="small" sx={{ color: cores.laranja }} />
              <Typography variant="subtitle2" sx={{ color: cores.laranja }}>Projeção de Lucro</Typography>
            </Stack>
            <Stack direction="row" gap={2}>
              <TextField size="small" label="Comissão (%)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.comissao}
                onChange={(e) => setComp({ ...comp, comissao: e.target.value })} />
              <TextField size="small" label="Impostos (%)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.impostos}
                onChange={(e) => setComp({ ...comp, impostos: e.target.value })} />
              <TextField size="small" label="Margem Bruta (%)" sx={sfFull}
                inputProps={{ type: 'number' }} value={comp.margembruta}
                onChange={(e) => setComp({ ...comp, margembruta: e.target.value })} />
            </Stack>

            <Divider />

            {res && (
              <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <ReceiptIcon fontSize="small" sx={{ color: cores.laranja }} />
                  <Typography variant="subtitle2" sx={{ color: cores.laranja }}>Resultado</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Total Materiais:</Typography>
                  <Typography variant="body2" fontWeight="bold">{fmtBRL(res.totalMat)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Total Serviços:</Typography>
                  <Typography variant="body2" fontWeight="bold">{fmtBRL(res.totalServ)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Comissão (R$):</Typography>
                  <Typography variant="body2">{fmtBRL(res.comissaoR)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Impostos (R$):</Typography>
                  <Typography variant="body2">{fmtBRL(res.impostosR)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Total Despesas:</Typography>
                  <Typography variant="body2">{fmtBRL(res.totalDesp)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold">Preço de Venda:</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: cores.laranja }}>{fmtBRL(res.pv)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold">Lucro Bruto:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">{fmtBRL(res.lucroBruto)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold">Lucro Bruto (%):</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">{res.lucroPct.toFixed(2)}%</Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" color="error" onClick={() => setModalComp(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={salvarComposicao}>Salvar Composição</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

AddKitBombeamento.propTypes = {
  produto: PropTypes.object,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default AddKitBombeamento;
